import { Injectable } from '@angular/core';
import { UrlSecurityService } from './url-security.service';
import { sessionStorageKeys, localStorageKeys, indexedDBKeys } from 'src/app/core/enum';
import * as constant from '../constants/constants';
import { NgxIndexedDBService } from 'ngx-indexed-db';

@Injectable({
  providedIn: 'root'
})
export class StorageService {

  constructor(
    private urlService: UrlSecurityService,
    private indexedDBService: NgxIndexedDBService
  ) { }

  /**
   * Get the storage type to use
   * @param key the key to be added in storage
   */
  getStorageType(key: string) {
    if (key in sessionStorageKeys) {
      return constant.common.session_storage;
    }

    if (key in localStorageKeys) {
      return constant.common.local_storage;
    }

    if (key in indexedDBKeys) {
      return constant.common.indexed_db;
    }

    throw 'Invalid key!';
  }

  /**
   * Sets the value of given key in respective storage
   * @param key key to be added
   * @param value value to be added
   */
  set(key: string, value: any) {
    // check which storage to use for the given key
    let storage_type = this.getStorageType(key);

    // call the respective service
    switch (storage_type) {
      case constant.common.session_storage: {
        sessionStorage.setItem(key, this.urlService.encryptUsingAES256(JSON.stringify(value)));
        break;
      }
      case constant.common.local_storage: {
        localStorage.setItem(key, this.urlService.encryptUsingAES256(JSON.stringify(value)));
        break;
      }
      case constant.common.indexed_db: {
        this.indexedDBService
          .update(constant.common.indexed_db_store, { config_key: key, config_value: this.urlService.encryptUsingAES256(JSON.stringify(value)) })
          .subscribe((key) => {
          });
      }
    }
  }

  /**
   * Gets the value of given key from respective storage
   * @param key key to be fetched
   */
  async get(key: string) {
    const storageType = this.getStorageType(key);

    switch (storageType) {
      case constant.common.session_storage:
        return this.getFromSessionStorage(key);

      case constant.common.local_storage:
        return this.getFromLocalStorage(key);

      case constant.common.indexed_db:
        return await this.getFromIndexedDB(key);

      default:
        return null;
    }
  }

  /**
   * Helper function to get data from session storage
   * @param key
   * @returns decrypted value
   */
  getFromSessionStorage(key: string) {
    const data = sessionStorage.getItem(key);
    return data? JSON.parse(this.urlService.decryptUsingAES256(data)) : data
  }

  /**
   * Helper function to get data from local storage
   * @param key
   * @returns decrypted value
   */
  getFromLocalStorage(key: string) {
    const data = localStorage.getItem(key);
    return data? JSON.parse(this.urlService.decryptUsingAES256(data)) : data
  }

  /**
   * Helper function to get data from indexedDB
   * @param key
   * @returns decrypted value
   */
  getFromIndexedDB(key: string) {
    return new Promise((resolve, reject) => {
      this.indexedDBService.getByKey(constant.common.indexed_db_store, key).subscribe((result: any) => {
        resolve(JSON.parse(this.urlService.decryptUsingAES256(result['config_value'])))
      }, error => {
        reject(error);
      });
    });
  }

  /**
   * Deletes the given key-value from respective storage
   * @param key key to be deleted
   */
  delete(key: string) {
    // check which storage to use for the given key
    let storage_type = this.getStorageType(key);

    // call the respective service
    switch (storage_type) {
      case constant.common.session_storage: {
        sessionStorage.removeItem(key);
        break;
      }
      case constant.common.local_storage: {
        localStorage.removeItem(key);
        break;
      }
      case constant.common.indexed_db: {
        this.indexedDBService.delete(constant.common.indexed_db_store, key).subscribe((response) => {

        });
      }
    }
  }

  /**
   * Clears the given storage
   * @param storage_type the storage type to be cleared
   */
  clear(storage_type: string) {
    // call the respective service
    switch (storage_type) {
      case constant.common.session_storage: {
        sessionStorage.clear()
        break;
      }

      case constant.common.local_storage: {
        localStorage.clear()
        break;
      }

      case constant.common.indexed_db: {
        this.indexedDBService.clear(constant.common.indexed_db_store).subscribe((successDeleted) => {

        });
      }
    }
  }
}